"use client";

import { DiscoverTune } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { Button } from "@/components/ui/Button";
import { Typography } from "@/components/ui/Typography";
import { FilterModalTrigger } from "@/features/products/ProductListingPage/components/FilterModal";
import { usePLP } from "@/features/products/ProductListingPage/hooks/PLPContext";
import { useProductListStateValue } from "@/features/products/ProductListingPage/hooks/useProductListState";
import { useTranslations } from "@/i18n/client";

interface FloatingFilterBarProps {
	/**
	 * Whether the floating bar should be visible. The Filterbar drives this from
	 * an IntersectionObserver on the inline bar (out of view) + a
	 * `useFooterProximity` check (footer not yet near).
	 */
	visible: boolean;
}

/**
 * Bottom-anchored, design-system-styled mirror of the inline Filterbar — gives
 * the user a way to open the filter modal and see how many filters are active
 * once they've scrolled past the page's own Filterbar. Slides up from the
 * bottom on visibility change; hidden + transformed away when `visible=false`
 * so it can't be tabbed to and doesn't block clicks on content below.
 *
 * Modelled on amundsen's `FloatingFilter` (same hide-near-footer pattern) but
 * built with the ellaandil design tokens: `bg-background-bg-neutral-1`,
 * `border-border-medium`, and the solid-primary `Button`.
 */
export const FloatingFilterBar = ({ visible }: FloatingFilterBarProps) => {
	const t = useTranslations();
	const { total } = useProductListStateValue();
	const { filters: selectedByKey } = usePLP();
	const totalSelected = Object.values(selectedByKey).reduce((sum, vs) => sum + vs.length, 0);

	return (
		<div
			aria-hidden={!visible}
			className={clsx(
				"fixed bottom-4 left-0 right-0 z-50 flex justify-center px-grid-section-horizontal-margin-page-base pointer-events-none",
				"transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
				visible ? "opacity-100" : "opacity-0",
			)}
		>
			<div
				className={clsx(
					"pointer-events-auto",
					"bg-background-bg-neutral-1 border border-border-medium rounded-component-specific-button shadow-lg",
					"flex items-center gap-grid-component-spacing-md",
					"px-grid-component-spacing-md py-grid-component-spacing-2xs",
					"transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
					visible ? "translate-y-0" : "translate-y-6",
				)}
			>
				{total > 0 && (
					<Typography variant="body-sm-reg" className="whitespace-nowrap text-text-dark-text-1">
						{total} {t("products.label")}
					</Typography>
				)}
				<FilterModalTrigger asChild>
					<Button
						variant="solid"
						color="primary"
						size="medium"
						className="flex items-center gap-0.5"
					>
						<DiscoverTune width={16} height={16} />
						<span>
							{t("Filter")}
							{totalSelected > 0 && ` (${totalSelected})`}
						</span>
					</Button>
				</FilterModalTrigger>
			</div>
		</div>
	);
};
